// src/components/dashboard/UserManagement.js
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, UserPlus, Edit, Trash2, CheckCircle, XCircle, X, 
  Search, Filter, Mail, Phone, Shield, Calendar, UserCheck,
  Star, Clock, Award, TrendingUp, MoreVertical, ExternalLink
} from 'lucide-react';
import axios from 'axios';

function UserManagement({ isMobile }) {
  const [users, setUsers] = useState([]);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'customer',
    isVerified: false
  });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, filters]);

  const fetchUsers = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: "getUsers",
        page: currentPage,
        limit: itemsPerPage,
        filters: filters
      });
      
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Mock data for demo
      const mockUsers = Array.from({ length: 25 }, (_, i) => ({
        id: i + 1,
        name: ['John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Wilson'][i % 4],
        email: `user${i + 1}@example.com`,
        phone: '+263 78' + (3907172 + i),
        role: i % 3 === 0 ? 'staff' : 'customer',
        isVerified: i % 2 === 0,
        createdAt: new Date(Date.now() - i * 86400000).toISOString(),
        lastLogin: i % 5 === 0 ? new Date().toISOString() : null,
        orderCount: Math.floor(Math.random() * 20),
        totalSpent: Math.floor(Math.random() * 1000) + 100,
        avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${i}`
      }));
      setUsers(mockUsers);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchQuery === '' || 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.phone.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilters = Object.keys(filters).every(key => {
      if (!filters[key]) return true;
      return String(user[key]).toLowerCase().includes(filters[key].toLowerCase());
    });
    
    return matchesSearch && matchesFilters;
  });

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: "createUser",
        ...userForm
      });

      if (response.data.success) {
        await fetchUsers();
        setIsAddingUser(false);
        setUserForm({ name: '', email: '', phone: '', role: 'customer', isVerified: false });
        alert('User created successfully!');
      }
    } catch (error) {
      console.error('Create user error:', error);
      alert('Failed to create user.');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: "updateUser",
        id: editingUser.id,
        ...userForm
      });

      if (response.data.success) {
        await fetchUsers();
        setEditingUser(null);
        setUserForm({ name: '', email: '', phone: '', role: 'customer', isVerified: false });
        alert('User updated successfully!');
      }
    } catch (error) {
      console.error('Update user error:', error);
      alert('Failed to update user.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
          apiType: "deleteUser",
          userId: userId
        });

        if (response.data.success) {
          await fetchUsers();
          alert('User deleted successfully!');
        }
      } catch (error) {
        console.error('Delete user error:', error);
        alert('Failed to delete user.');
      }
    }
  };

  const handleToggleVerification = async (userId, currentStatus) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/index.php`, {
        apiType: "toggleUserVerification",
        userId: userId,
        isVerified: !currentStatus
      });

      if (response.data.success) {
        await fetchUsers();
        alert(`User ${!currentStatus ? 'verified' : 'unverified'} successfully!`);
      }
    } catch (error) {
      console.error('Toggle verification error:', error);
      alert('Failed to update verification status.');
    }
  };

  const stats = {
    total: filteredUsers.length,
    customers: filteredUsers.filter(u => u.role === 'customer').length,
    staff: filteredUsers.filter(u => u.role === 'staff').length,
    verified: filteredUsers.filter(u => u.isVerified).length,
    active: filteredUsers.filter(u => u.lastLogin && new Date(u.lastLogin) > new Date(Date.now() - 30 * 86400000)).length
  };

  const StatCard = ({ label, value, icon: Icon, color, delay }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="relative"
    >
      <div className={`absolute -inset-1 bg-gradient-to-r from-${color}-500/10 rounded-2xl blur-xl`}></div>
      <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-xl bg-gradient-to-r from-${color}-500 to-${color}-600`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <TrendingUp className="w-5 h-5 text-gray-400" />
        </div>
        <div className="text-3xl font-bold text-white mb-2">{value}</div>
        <div className="text-sm text-gray-400">{label}</div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-600 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            User Management
          </h2>
          <p className="text-gray-400">Manage customer and staff accounts</p>
        </div>
        
        <motion.button 
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsAddingUser(true)}
          className="px-4 py-3 bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-xl font-bold hover:shadow-lg transition-all flex items-center space-x-2"
        >
          <UserPlus className="w-5 h-5" />
          <span>Add User</span>
        </motion.button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard label="Total Users" value={stats.total} icon={Users} color="blue" delay={0} />
        <StatCard label="Customers" value={stats.customers} icon={Users} color="green" delay={0.1} />
        <StatCard label="Staff Members" value={stats.staff} icon={Shield} color="purple" delay={0.2} />
        <StatCard label="Verified" value={stats.verified} icon={UserCheck} color="emerald" delay={0.3} />
        <StatCard label="Active (30d)" value={stats.active} icon={TrendingUp} color="amber" delay={0.4} />
      </div>

      {/* Search and Filters */}
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-gray-800/20 to-gray-900/20 rounded-3xl blur-xl"></div>
        <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 p-6">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="flex space-x-3">
              <select
                value={filters.role || ''}
                onChange={(e) => setFilters({ ...filters, role: e.target.value })}
                className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Roles</option>
                <option value="customer">Customer</option>
                <option value="staff">Staff</option>
              </select>
              
              <select
                value={filters.isVerified || ''}
                onChange={(e) => setFilters({ ...filters, isVerified: e.target.value })}
                className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Verified</option>
                <option value="false">Unverified</option>
              </select>
            </div>
          </div>

          {/* Users Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedUsers.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ y: -5 }}
                className="relative group"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                <div className="relative bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 overflow-hidden">
                  {/* User Header */}
                  <div className="p-6 border-b border-gray-800">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl overflow-hidden border-2 border-gray-700">
                          <img 
                            src={user.avatar} 
                            alt={user.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div>
                          <h4 className="font-bold text-white">{user.name}</h4>
                          <p className="text-sm text-gray-400">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleToggleVerification(user.id, user.isVerified)}
                          className={`p-2 rounded-lg ${user.isVerified ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}
                        >
                          {user.isVerified ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs ${user.role === 'staff' ? 'bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400' : 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-blue-400'}`}>
                        {user.role}
                      </span>
                      {user.isVerified && (
                        <span className="px-3 py-1 bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-400 rounded-full text-xs">
                          Verified
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-800/30 rounded-lg p-3">
                        <div className="text-sm text-gray-400">Orders</div>
                        <div className="text-lg font-bold text-white">{user.orderCount || 0}</div>
                      </div>
                      <div className="bg-gray-800/30 rounded-lg p-3">
                        <div className="text-sm text-gray-400">Spent</div>
                        <div className="text-lg font-bold text-emerald-400">${user.totalSpent || 0}</div>
                      </div>
                    </div>
                  </div>

                  {/* User Details */}
                  <div className="p-6">
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-400">{user.phone}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-400">
                          Joined {new Date(user.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      {user.lastLogin && (
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-400">
                            Last login {new Date(user.lastLogin).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingUser(user);
                            setUserForm({
                              name: user.name,
                              email: user.email,
                              phone: user.phone,
                              role: user.role,
                              isVerified: user.isVerified
                            });
                          }}
                          className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <button className="text-gray-400 hover:text-white">
                        <MoreVertical className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          {filteredUsers.length > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between px-6 py-4 border-t border-gray-800 mt-6">
              <div className="text-sm text-gray-400 mb-4 md:mb-0">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm font-medium text-white">
                  Page {currentPage} of {totalPages}
                </span>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <AnimatePresence>
        {(isAddingUser || editingUser) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-gray-800 shadow-2xl w-full max-w-2xl"
            >
              <div className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 flex items-center justify-center">
                      <UserPlus className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {editingUser ? 'Edit User' : 'Add New User'}
                    </h3>
                  </div>
                  <button 
                    onClick={() => {
                      setIsAddingUser(false);
                      setEditingUser(null);
                      setUserForm({ name: '', email: '', phone: '', role: 'customer', isVerified: false });
                    }}
                    className="p-2 hover:bg-gray-800 rounded-full transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-400" />
                  </button>
                </div>

                <form onSubmit={editingUser ? handleEditUser : handleCreateUser}>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Full Name</label>
                        <div className="relative">
                          <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="text"
                            name="name"
                            value={userForm.name}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter full name"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="email"
                            name="email"
                            value={userForm.email}
                            onChange={handleInputChange}
                            required
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter email address"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                          <input
                            type="tel"
                            name="phone"
                            value={userForm.phone}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter phone number"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Role</label>
                        <select
                          name="role"
                          value={userForm.role}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="customer">Customer</option>
                          <option value="staff">Staff</option>
                        </select>
                      </div>
                    </div>

                    {editingUser && (
                      <div>
                        <label className="flex items-center space-x-3 cursor-pointer">
                          <div className="relative">
                            <input
                              type="checkbox"
                              name="isVerified"
                              checked={userForm.isVerified}
                              onChange={(e) => setUserForm(prev => ({ ...prev, isVerified: e.target.checked }))}
                              className="sr-only"
                            />
                            <div className={`w-6 h-6 rounded border ${userForm.isVerified ? 'bg-emerald-600 border-emerald-600' : 'bg-gray-700 border-gray-600'}`}>
                              {userForm.isVerified && <CheckCircle className="w-4 h-4 text-white m-auto" />}
                            </div>
                          </div>
                          <span className="text-gray-300">Verified Account</span>
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-4 mt-8">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl font-bold hover:shadow-lg transition-all"
                    >
                      {editingUser ? 'Update User' : 'Create User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddingUser(false);
                        setEditingUser(null);
                        setUserForm({ name: '', email: '', phone: '', role: 'customer', isVerified: false });
                      }}
                      className="flex-1 px-6 py-3 bg-gray-800 border border-gray-700 text-gray-300 rounded-xl font-bold hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default UserManagement;